/**
 * Ask Panel — الـAI مدمج أسفل النتيجة فقط. لا Chatbot عائم (Design Freeze).
 */

import { useState } from 'react';
import { color } from '@/design/tokens';
import type { ValuationResult } from '@/domain/types';
import { AI_SCENARIO_WARNING, PRESET_QUESTIONS, answerQuestion, type AskAnswer } from '@/services/ask';
import { Button, Chip } from '@/components/primitives/Primitives';

export function AskPanel({ result }: { result: ValuationResult }): JSX.Element {
  const [answers, setAnswers] = useState<AskAnswer[]>([]);
  const [free, setFree] = useState('');

  function ask(question: string): void {
    if (!question.trim()) return;
    setAnswers((prev) => [...prev, answerQuestion(question, result)]);
  }

  return (
    <section className="nq-card" aria-label="اسأل نطاق" data-testid="ask-panel">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span
          className="nq-pulse"
          aria-hidden="true"
          style={{ width: 7, height: 7, borderRadius: '50%', background: color.teal }}
        />
        <h2 style={{ fontSize: 17, fontWeight: 600 }}>اسأل نطاق</h2>
      </div>
      <p style={{ marginTop: 8, fontSize: 12.5, color: color.textSubtle }}>
        الإجابات تفسّر البيانات الظاهرة في هذه النتيجة فقط.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
        {PRESET_QUESTIONS.map((q) => (
          <Chip key={q} label={q} onClick={() => ask(q)} />
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
        <input
          className="nq-input"
          style={{ flex: '1 1 240px' }}
          placeholder="اكتب سؤالك عن هذه النتيجة..."
          value={free}
          onChange={(e) => setFree(e.target.value)}
          aria-label="سؤال حر"
        />
        <Button
          onClick={() => {
            ask(free);
            setFree('');
          }}
        >
          اسأل
        </Button>
      </div>

      <div style={{ display: 'grid', gap: 16, marginTop: 20 }}>
        {answers.map((a, i) => (
          <article
            key={`${a.question}-${i}`}
            style={{
              padding: 18,
              borderRadius: 14,
              background: color.surface700,
              border: `1px solid ${color.hairlineSoft}`,
            }}
          >
            <p style={{ fontSize: 13, color: color.textMuted, fontWeight: 500 }}>{a.question}</p>
            {a.scenario ? (
              <span
                data-testid="scenario-badge"
                style={{
                  display: 'inline-block',
                  marginTop: 10,
                  padding: '4px 9px',
                  borderRadius: 6,
                  border: '1px solid rgba(240,162,75,.32)',
                  color: color.amber,
                  fontSize: 11.5,
                  fontWeight: 600,
                }}
              >
                سيناريو افتراضي
              </span>
            ) : null}
            <p style={{ marginTop: 10, fontSize: 14, lineHeight: 1.75, color: color.textBody }}>
              {a.answer}
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 14 }}>
              {a.facts.map((f) => (
                <div
                  key={f.label}
                  style={{
                    flex: '1 1 160px',
                    padding: 12,
                    borderRadius: 12,
                    background: color.panel600,
                    border: `1px solid ${color.hairlineSoft}`,
                  }}
                >
                  <div className="nq-label">{f.label}</div>
                  <div className="nq-num" dir="ltr" style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}>
                    {f.value}
                  </div>
                </div>
              ))}
            </div>
            {a.scenario ? (
              <p
                style={{
                  marginTop: 14,
                  padding: '10px 12px',
                  borderRadius: 10,
                  background: 'rgba(240,162,75,.08)',
                  border: '1px solid rgba(240,162,75,.28)',
                  fontSize: 12.5,
                  color: color.amberText,
                }}
              >
                {AI_SCENARIO_WARNING}
              </p>
            ) : null}
          </article>
        ))}
      </div>
    </section>
  );
}
